import {
  dimensionGeometry,
  entityPaths,
  entityTextSpec,
  measureTextWidth,
  type Entity,
} from '@lakaran/geometry';
import type { CadDocument } from './document';

const PT_PER_MM = 72 / 25.4;

function num(value: number): string {
  return (Math.round(value * 1000) / 1000).toString();
}

function rgb(color: number): string {
  const r = ((color >> 16) & 0xff) / 255;
  const g = ((color >> 8) & 0xff) / 255;
  const b = (color & 0xff) / 255;
  return `${r.toFixed(3)} ${g.toFixed(3)} ${b.toFixed(3)}`;
}

function escapeText(text: string): string {
  return text.replace(/\\/g, '\\\\').replace(/\(/g, '\\(').replace(/\)/g, '\\)');
}

function pathOps(points: { x: number; y: number }[], closed: boolean): string {
  if (points.length < 2) return '';
  let out = `${num(points[0]!.x)} ${num(points[0]!.y)} m\n`;
  for (let index = 1; index < points.length; index++) {
    out += `${num(points[index]!.x)} ${num(points[index]!.y)} l\n`;
  }
  if (closed) out += 'h\n';
  return out;
}

function entityContent(entity: Entity, doc: CadDocument): string {
  const layer = doc.getLayer(entity.layerId);
  if (!layer || !layer.visible) return '';
  const color = rgb(entity.color ?? layer.color);
  const width = Math.max(0.2, (entity.lineWidth ?? layer.lineWidth) * 0.3);

  let out = `q\n${color} RG\n${color} rg\n${width.toFixed(2)} w\n1 J\n1 j\n`;

  for (const path of entityPaths(entity, { chordTolerance: 0.02 })) {
    out += pathOps(path.points, path.closed);
    out += 'S\n';
  }

  if (entity.type === 'dimension') {
    for (const arrow of dimensionGeometry(entity).arrows) {
      out += pathOps(arrow, true);
      out += 'f\n';
    }
  }

  const spec = entityTextSpec(entity);
  if (spec) {
    const size = spec.height * PT_PER_MM;
    const cos = Math.cos(spec.rotation);
    const sin = Math.sin(spec.rotation);
    const widthMm = measureTextWidth(spec.text, spec.height, spec.widthFactor);
    let x = spec.position.x;
    if (spec.anchor === 'center') x -= widthMm / 2;
    else if (spec.anchor === 'right') x -= widthMm;
    const y = spec.position.y;
    const matrix = `${cos.toFixed(4)} ${sin.toFixed(4)} ${(-sin).toFixed(4)} ${cos.toFixed(4)} ${num(x)} ${num(y)}`;
    out += `BT\n/F1 ${size.toFixed(2)} Tf\n${color} rg\n${matrix} Tm\n(${escapeText(spec.text)}) Tj\nET\n`;
  }

  out += 'Q\n';
  return out;
}

/** Vector PDF export at 1:1 (1 mm = 72/25.4 pt), page sized to the drawing. */
export function documentToPdf(doc: CadDocument, marginMm = 10): Uint8Array<ArrayBuffer> {
  const box = doc.bounds(true);
  const margin = marginMm * PT_PER_MM;
  const drawingWidth = box.isEmpty ? 100 : Math.max(box.width, 1);
  const drawingHeight = box.isEmpty ? 100 : Math.max(box.height, 1);
  const pageWidth = Math.min(14000, drawingWidth * PT_PER_MM + margin * 2);
  const pageHeight = Math.min(14000, drawingHeight * PT_PER_MM + margin * 2);

  let content = `q\n${PT_PER_MM.toFixed(6)} 0 0 ${PT_PER_MM.toFixed(6)} 0 0 cm\n`;
  content += '1 J\n1 j\n';
  for (const entity of doc.all()) content += entityContent(entity, doc);
  content += 'Q\n';

  const objects: string[] = [
    '<< /Type /Catalog /Pages 2 0 R >>',
    '<< /Type /Pages /Kids [3 0 R] /Count 1 >>',
    `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${num(pageWidth)} ${num(pageHeight)}] /Resources << /Font << /F1 5 0 R >> >> /Contents 4 0 R >>`,
    `<< /Length ${content.length} >>\nstream\n${content}endstream`,
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>',
  ];

  let pdf = '%PDF-1.4\n';
  const offsets: number[] = [];
  for (let index = 0; index < objects.length; index++) {
    offsets.push(pdf.length);
    pdf += `${index + 1} 0 obj\n${objects[index]}\nendobj\n`;
  }

  const xrefStart = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  for (const offset of offsets) pdf += `${offset.toString().padStart(10, '0')} 00000 n \n`;
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefStart}\n%%EOF\n`;

  const bytes = new Uint8Array(pdf.length);
  for (let index = 0; index < pdf.length; index++) bytes[index] = pdf.charCodeAt(index) & 0xff;
  return bytes;
}
